import { Method, EventEmitter, Event, h, Component, Prop, Element, Listen } from '@stencil/core';

import { KEYBOARD } from '../../../../config/constants';
/**
 * @part base - Main container inside the component's shadow DOM
 */
@Component({
  tag: 'pk-menu-subitem',
  styleUrl: './scss/pk-menu-subitem.scss',
  shadow: {
    delegatesFocus: true,
  },
})
export class PkMenuSubitem {
  // Own Properties
  // ====================

  private mainElem!: HTMLElement;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkMenuSubitemElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** If true, the menu-subitem will be shown as active/focus. */
  @Prop({ reflect: true }) active: boolean = false;

  /** If true, the menu-subitem will be shown in a disabled state. */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** This number represents the index of the menu sub item  */
  @Prop({ reflect: true }) level: number = 1;

  /**
   * @internal
   * If true, the border left of the item will be highlighted when selected
   */
  @Prop({ reflect: true }) highlight: boolean = false;

  /** If provided, the menu-subitem will be treated as a link and it set the URL that the hyperlink points to. */
  @Prop({ reflect: true }) href: string;

  /**
   * Specifies where to display the linked URL, as the name for a browsing context (a tab, window, or <iframe>).
   * Details: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target
   */
  @Prop({ reflect: true }) linkTarget?: string = '_self';

  /** Specifies the relationship between the current page and the linked page */
  @Prop({ reflect: true }) linkRel?: string;

  /** If true, the menu-subitem will be shown as selected. */
  @Prop({ reflect: true }) selected: boolean = false;

  /** A unique value to store in the menu-subitem. */
  @Prop({ reflect: true }) value: string;

  // Prop lifecycle events
  // =======================

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler to be called when the subitem is focused */
  @Event() pkFocus: EventEmitter;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  // Listeners
  // ==============

  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    if (event.target !== this.el || !this.mainElem) return;

    const isEnterOrSpace = event.key === KEYBOARD.Enter || event.key === KEYBOARD.Space;
    if (isEnterOrSpace) {
      event.preventDefault();
      this.mainElem.click();
    }
  }

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  /** Apply focus to the menu-subitem */
  @Method()
  async focusMenuSubitem() {
    this.mainElem.focus();
  }

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private handleFocus = () => {
    this.pkFocus.emit();
  };

  private get isLink(): boolean {
    return !!this.href;
  }

  private get siblingsElements(): HTMLPkMenuSubitemElement[] {
    return Array.from(this.el.parentNode.children) as HTMLPkMenuSubitemElement[];
  }

  private get hasSelectedSibling(): boolean {
    if (!this.siblingsElements.length) return false;

    return this.siblingsElements.some((item) => item.selected);
  }

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const TagElem = this.isLink ? 'a' : 'button';

    return (
      <TagElem
        ref={(el) => (this.mainElem = el)}
        class={{
          'pk-menu__submenu': true,
          [`level-${this.level}`]: true,
          selected: this.selected,
          disabled: this.disabled,
          highlight: this.highlight,
          bordered: this.hasSelectedSibling && this.level === 3,
        }}
        onFocus={this.handleFocus}
        href={this.isLink ? this.href : undefined}
        rel={this.isLink ? this.linkRel : undefined}
        target={this.isLink ? this.linkTarget : undefined}
        aria-disabled={this.disabled ? 'true' : 'false'}
        aria-current={this.selected ? 'true' : 'false'}
        tabIndex={this.disabled ? -1 : undefined}
        role="menuitem"
        part="base"
      >
        <slot />
      </TagElem>
    );
  }
}
